import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { showErrorToast, showSuccessToast } from 'components/Toast';
import BaseInput from 'components/Inputs/BaseInput';
import SolidButton from 'components/SolidButton';

import { IBank, IBankAccount } from 'utils/interfaces';
import { ERROR_MESSAGES, REQUIRED_RULE } from 'utils/constants';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';
import { BANK_ACCOUNT_KINDS } from 'utils/enums';
import { cleanObject } from 'utils/functions';
import StorageService from 'utils/storage';

export default function BankAccountModalContent({
  title,
  defaultValues,
  editData,
  onClose,
  onSuccess,
}: {
  title: string;
  defaultValues?: any;
  editData?: IBankAccount;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: editData ||
      defaultValues || {
        bank: 'BCP',
        account_type: BANK_ACCOUNT_KINDS.A,
      },
  });
  const history = useNavigate();
  const auth = useAuth();

  const apiUrl =
    StorageService.get('userData')?.level === '30' ? '/bank_accounts' : '/company_bank_account';

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const bankCode = watch('code');
  const accountType = watch('account_type', BANK_ACCOUNT_KINDS.A);

  const [loading, setLoading] = useState(false);
  const [banks, setBanks] = useState<Array<IBank>>([]);

  const submitAction = async (data: any) => {
    setLoading(true);
    let result: any = await (editData
      ? generalApi.put(apiUrl, {
          ...cleanObject(data),
          bankAccId: editData._id,
        })
      : generalApi.post(apiUrl, cleanObject(data)));
    setLoading(false);
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast(editData ? 'Cambios guardados' : 'Cuenta bancaria creada');
    onSuccess();
  };

  useEffect(() => {
    const getBanks = async () => {
      const result = await generalApi.get('/active_banks');
      if (!result.success) {
        showErrorToast(result.message);
        return;
      }
      if (result.data) {
        setBanks(result.data);
      }
    };
    getBanks();
  }, [generalApi]);

  const buildBaseInput = (
    label: string,
    type: React.HTMLInputTypeAttribute,
    name: string,
    rules: any,
    props: any
  ) => {
    return (
      <BaseInput
        label={label}
        type={type}
        name={name}
        register={register}
        errors={errors}
        rules={rules}
        watch={watch}
        control={control}
        {...props}
      />
    );
  };
  return (
    <form onSubmit={handleSubmit(submitAction)}>
      <div className="rounded-t-lg bg-primary-600 px-4 pb-4 sm:flex sm:items-start sm:p-6 sm:py-4">
        <div className="mt-3 text-center sm:mt-0 sm:text-left">
          <h3 className="text-lg font-medium leading-6 text-white" id="modal-headline">
            {title || 'Confirmar acción'}
          </h3>
        </div>
      </div>
      <div className="space-y-4 px-4 py-5 sm:p-6">
        {buildBaseInput('Banco', 'select', 'code', REQUIRED_RULE, {
          options: banks.map((bank: IBank) => ({ value: bank.code, name: bank.name })),
        })}
        {buildBaseInput(
          'Número de cuenta',
          'text',
          'number',
          {
            ...REQUIRED_RULE,
            minLength: {
              value: 10,
              message: ERROR_MESSAGES.MIN_LENGTH(10),
            },
            maxLength: {
              value: banks.find((obj) => obj.code === bankCode)?.[
                `${accountType}_length` as 'A_length' | 'C_length'
              ],
              message: ERROR_MESSAGES.MAX_LENGTH(
                banks.find((obj) => obj.code === bankCode)?.[
                  `${accountType}_length` as 'A_length' | 'C_length'
                ] || 0
              ),
            },
          },
          {}
        )}
        {buildBaseInput(
          `Código de Cuenta Interbancario ${
            banks.find((obj) => obj.code === bankCode)?.is_interbank ? '' : '(Opcional)'
          }`,
          'text',
          'cci',
          {
            required: banks.find((obj) => obj.code === bankCode)?.is_interbank
              ? REQUIRED_RULE.required
              : false,
            minLength: {
              value: 20,
              message: ERROR_MESSAGES.LENGTH(20),
            },
            maxLength: {
              value: 20,
              message: ERROR_MESSAGES.LENGTH(20),
            },
          },
          {}
        )}
        {buildBaseInput('Tipo de cuenta', 'select', 'account_type', REQUIRED_RULE, {
          options: [
            { value: BANK_ACCOUNT_KINDS.A, name: 'Ahorros' },
            { value: BANK_ACCOUNT_KINDS.C, name: 'Corriente' },
          ],
        })}
        {buildBaseInput('Moneda', 'select', 'currency', REQUIRED_RULE, {
          options: [
            { value: 'PEN', name: 'Soles' },
            { value: 'USD', name: 'Dólares' },
          ],
        })}
        {buildBaseInput('Descripción (Opcional)', 'text', 'description', {}, {})}
      </div>
      <div className="rounded-b-lg bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
        <SolidButton
          className="w-auto sm:ml-3"
          isLoading={loading}
          onClick={() => handleSubmit(submitAction)}
        >
          {editData ? 'Guardar cambios' : 'Crear'}
        </SolidButton>
        <button
          type="button"
          onClick={onClose}
          className="focus:ring-primary-500 mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
